import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { eventsApi, Event } from '../services/api';
import { Plus, Calendar, Users, Clock, MoreVertical, Send, X, Eye } from 'lucide-react';
import { format } from 'date-fns';

type StatusFilter = 'all' | 'draft' | 'sent' | 'closed' | 'canceled';

export function DashboardPage() {
    const [events, setEvents] = useState<Event[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);

    useEffect(() => {
        loadEvents();
    }, []);

    const loadEvents = async () => {
        try {
            setIsLoading(true);
            const data = await eventsApi.list();
            setEvents(data);
        } catch (err: any) {
            setError(err.message || 'Failed to load events');
        } finally {
            setIsLoading(false);
        }
    };

    const filteredEvents = statusFilter === 'all'
        ? events
        : events.filter(e => e.status === statusFilter);

    const handleCancel = async (id: string) => {
        if (!confirm('Are you sure you want to cancel this event?')) return;
        try {
            await eventsApi.cancel(id);
            loadEvents();
        } catch (err: any) {
            alert(err.message || 'Failed to cancel event');
        }
        setActionMenuOpen(null);
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this event?')) return;
        try {
            await eventsApi.delete(id);
            loadEvents();
        } catch (err: any) {
            alert(err.message || 'Failed to delete event');
        }
        setActionMenuOpen(null);
    };

    const getStatusBadge = (status: Event['status']) => {
        const badges: Record<Event['status'], string> = {
            draft: 'badge-draft',
            sent: 'badge-sent',
            closed: 'badge-closed',
            canceled: 'badge-canceled',
        };
        return `badge ${badges[status]}`;
    };

    if (isLoading) {
        return (
            <div className="page container">
                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
                    <div className="spinner" />
                </div>
            </div>
        );
    }

    return (
        <div className="page container">
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--space-6)'
            }}>
                <h1>My Events</h1>
                <Link to="/events/new" className="btn btn-primary">
                    <Plus size={18} />
                    Create New Event
                </Link>
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            {/* Status Filter */}
            <div style={{
                display: 'flex',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-5)',
                flexWrap: 'wrap'
            }}>
                {(['all', 'draft', 'sent', 'closed', 'canceled'] as StatusFilter[]).map((status) => (
                    <button
                        key={status}
                        onClick={() => setStatusFilter(status)}
                        className={`btn btn-sm ${statusFilter === status ? 'btn-primary' : 'btn-secondary'}`}
                    >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                ))}
            </div>

            {/* Events Grid */}
            {filteredEvents.length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                    <Calendar size={48} style={{ color: 'var(--color-gray-300)', marginBottom: 'var(--space-4)' }} />
                    <h3 style={{ marginBottom: 'var(--space-2)' }}>No events yet</h3>
                    <p style={{ color: 'var(--color-gray-500)', marginBottom: 'var(--space-5)' }}>
                        Create your first event to start scheduling meetings
                    </p>
                    <Link to="/events/new" className="btn btn-primary">
                        <Plus size={18} />
                        Create New Event
                    </Link>
                </div>
            ) : (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: 'var(--space-4)'
                }}>
                    {filteredEvents.map((event) => (
                        <div key={event.id} className="card" style={{ position: 'relative' }}>
                            {/* Action Menu */}
                            <div style={{ position: 'absolute', top: '16px', right: '16px' }}>
                                <button
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => setActionMenuOpen(actionMenuOpen === event.id ? null : event.id)}
                                >
                                    <MoreVertical size={18} />
                                </button>
                                {actionMenuOpen === event.id && (
                                    <div style={{
                                        position: 'absolute',
                                        right: 0,
                                        top: '100%',
                                        background: 'white',
                                        border: '1px solid var(--color-gray-200)',
                                        borderRadius: 'var(--radius-md)',
                                        boxShadow: 'var(--shadow-lg)',
                                        zIndex: 10,
                                        minWidth: '150px'
                                    }}>
                                        <Link
                                            to={`/events/${event.id}`}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '12px 16px',
                                                color: 'var(--color-gray-700)',
                                                borderBottom: '1px solid var(--color-gray-100)'
                                            }}
                                        >
                                            <Eye size={16} /> View Details
                                        </Link>
                                        {event.status === 'draft' && (
                                            <Link
                                                to={`/events/${event.id}/edit`}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '12px 16px',
                                                    color: 'var(--color-gray-700)',
                                                    borderBottom: '1px solid var(--color-gray-100)'
                                                }}
                                            >
                                                <Send size={16} /> Edit & Send
                                            </Link>
                                        )}
                                        {event.status === 'sent' && (
                                            <button
                                                onClick={() => handleCancel(event.id)}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '12px 16px',
                                                    color: 'var(--color-error)',
                                                    background: 'none',
                                                    border: 'none',
                                                    width: '100%',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <X size={16} /> Cancel Event
                                            </button>
                                        )}
                                        {event.status === 'draft' && (
                                            <button
                                                onClick={() => handleDelete(event.id)}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '12px 16px',
                                                    color: 'var(--color-error)',
                                                    background: 'none',
                                                    border: 'none',
                                                    width: '100%',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <X size={16} /> Delete
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Event Content */}
                            <div style={{ marginBottom: 'var(--space-4)' }}>
                                <span className={getStatusBadge(event.status)}>
                                    {event.status}
                                </span>
                            </div>

                            <h3 style={{ marginBottom: 'var(--space-2)', paddingRight: '40px' }}>
                                <Link to={`/events/${event.id}`} style={{ color: 'inherit' }}>
                                    {event.event_name}
                                </Link>
                            </h3>

                            <div style={{
                                display: 'flex',
                                gap: 'var(--space-4)',
                                color: 'var(--color-gray-500)',
                                fontSize: 'var(--font-size-sm)',
                                marginBottom: 'var(--space-3)'
                            }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Users size={14} />
                                    {event.recipient_count} recipient{event.recipient_count !== 1 ? 's' : ''}
                                </span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Clock size={14} />
                                    {event.duration_minutes} min
                                </span>
                            </div>

                            {event.status === 'sent' && (
                                <div style={{
                                    background: 'var(--color-gray-100)',
                                    padding: 'var(--space-3)',
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: 'var(--font-size-sm)'
                                }}>
                                    <strong>{event.response_count}</strong> of {event.recipient_count} responded
                                </div>
                            )}

                            <div style={{
                                marginTop: 'var(--space-3)',
                                paddingTop: 'var(--space-3)',
                                borderTop: '1px solid var(--color-gray-100)',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-gray-500)'
                            }}>
                                Created {format(new Date(event.created_at), 'MMM d, yyyy')}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
